import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import { NavSidebar } from './components/NavSidebar';
import DataTable from 'react-data-table-component';
import { Modal, Button, Form, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaList, FaSearch, FaTimes, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { MdArrowBack } from 'react-icons/md';
import PropertyList from '../models/PropertyList';
import Property from '../models/Property';
import _ from 'lodash';

const PropertyListScreen = () => {
    const { user, isAdminOrHigher, isSubdomainAdminOrHigher } = useAuth();
    const history = useHistory();
    
    const [propertyLists, setPropertyLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedList, setSelectedList] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        properties_list: []
    });
    const [availableProperties, setAvailableProperties] = useState([]);
    const [loadingProperties, setLoadingProperties] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [propertySearchTerm, setPropertySearchTerm] = useState('');
    const [selectedExistingLists, setSelectedExistingLists] = useState([]);
    const [showImportSection, setShowImportSection] = useState(false);

    // Filter properties based on search term
    const filteredProperties = useMemo(() => {
        if (!propertySearchTerm.trim()) {
            return availableProperties;
        }
        
        const searchLower = propertySearchTerm.toLowerCase();
        return availableProperties.filter(property => {
            const nameMatch = property.name?.toLowerCase().includes(searchLower);
            const addressMatch = property.address?.toLowerCase().includes(searchLower);
            return nameMatch || addressMatch;
        });
    }, [availableProperties, propertySearchTerm]);

    // Sort filtered properties: selected first, then alphabetically
    const sortedFilteredProperties = useMemo(() => {
        return [...filteredProperties].sort((a, b) => {
            const aSelected = formData.properties_list.includes(a.uuid);
            const bSelected = formData.properties_list.includes(b.uuid);
            
            // If one is selected and the other isn't, selected comes first
            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;
            
            // If both have same selection status, sort alphabetically by name
            return (a.name || '').localeCompare(b.name || '');
        });
    }, [filteredProperties, formData.properties_list]);

    useEffect(() => {
        if (!isAdminOrHigher() && !isSubdomainAdminOrHigher()) {
            history.push('/');
            return;
        }
        fetchPropertyLists();
    }, []);

    const fetchPropertyLists = async () => {
        try {
            setLoading(true);
            setError(null);
            const lists = await PropertyList.getAll(user.company.id);
            console.log('Fetched property lists:', lists);
            setPropertyLists(lists || []);
        } catch (err) {
            console.error('Error fetching property lists:', err);
            setError('Failed to load property lists: ' + (err.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableProperties = async () => {
        try {
            setLoadingProperties(true);
            const properties = await Property.getAllPropertiesForCompany(user.company.id);
            setAvailableProperties(properties);
        } catch (err) {
            console.error('Error fetching properties:', err);
            setError('Failed to load available properties');
        } finally {
            setLoadingProperties(false);
        }
    };

    // When available properties finish loading during edit, remove stale UUIDs
    // that point to archived/deleted properties
    useEffect(() => {
        if (modalMode === 'edit' && availableProperties.length > 0 && formData.properties_list.length > 0) {
            const validUuids = new Set(availableProperties.map(p => p.uuid));
            const filtered = formData.properties_list.filter(uuid => validUuids.has(uuid));
            if (filtered.length !== formData.properties_list.length) {
                console.log(`[PropertyListScreen] Removed ${formData.properties_list.length - filtered.length} stale UUIDs from selection`);
                setFormData(prev => ({ ...prev, properties_list: filtered }));
            }
        }
    }, [availableProperties, modalMode]);

    const handleCreate = () => {
        setModalMode('create');
        setSelectedList(null);
        setFormData({
            name: '',
            properties_list: []
        });
        setPropertySearchTerm(''); // Reset search when opening modal
        setSelectedExistingLists([]); // Reset selected lists
        setShowImportSection(false); // Reset import section
        setShowModal(true);
        fetchAvailableProperties();
    };

    const handleEdit = (list) => {
        setModalMode('edit');
        setSelectedList(list);
        const rawList = typeof list.properties_list === 'string'
            ? JSON.parse(list.properties_list)
            : list.properties_list || [];
        const parsedProperties = [...new Set(rawList)];
        console.log(`[handleEdit] "${list.name}" - raw count: ${rawList.length}, unique count: ${parsedProperties.length}, typeof properties_list: ${typeof list.properties_list}`);
        setFormData({
            name: list.name,
            properties_list: parsedProperties
        });
        setPropertySearchTerm(''); // Reset search when opening modal
        setShowModal(true);
        fetchAvailableProperties();
    };

    const handleDelete = async (list) => {
        if (!window.confirm(`Are you sure you want to delete "${list.name}"?`)) {
            return;
        }

        try {
            setError(null);
            await PropertyList.delete(null, list.uuid);
            setSuccessMessage(`Property list "${list.name}" deleted successfully`);
            await fetchPropertyLists();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            console.error('Error deleting property list:', err);
            setError('Failed to delete property list: ' + (err.message || 'Unknown error'));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (submitting) return;
        
        try {
            setSubmitting(true);
            setError(null);
            const data = {
                company_id: user.company.id,
                name: formData.name,
                properties_list: JSON.stringify([...new Set(formData.properties_list)])
            };

            let result;
            if (modalMode === 'create') {
                result = await PropertyList.create(data);
                console.log('Created property list:', result);
                setSuccessMessage('Property list created successfully');
            } else {
                result = await PropertyList.update({
                    uuid: selectedList.uuid,
                    ...data
                });
                console.log('Updated property list:', result);
                setSuccessMessage('Property list updated successfully');
            }

            setShowModal(false);
            // Wait a moment for the backend to fully persist the data
            setTimeout(async () => {
                await fetchPropertyLists();
            }, 500);
            
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            console.error('Error saving property list:', err);
            setError('Failed to save property list: ' + (err.message || 'Unknown error'));
        } finally {
            setSubmitting(false);
        }
    };

    const handlePropertyToggle = (propertyUuid) => {
        setFormData(prev => ({
            ...prev,
            properties_list: prev.properties_list.includes(propertyUuid)
                ? prev.properties_list.filter(uuid => uuid !== propertyUuid)
                : [...prev.properties_list, propertyUuid]
        }));
    };

    const handleExistingListToggle = (listUuid) => {
        const isSelected = selectedExistingLists.includes(listUuid);
        
        if (!isSelected) {
            // Add list to selected lists
            setSelectedExistingLists(prev => [...prev, listUuid]);
            
            // Find the list and get its properties
            const list = propertyLists.find(l => l.uuid === listUuid);
            if (list) {
                const listProperties = typeof list.properties_list === 'string' 
                    ? JSON.parse(list.properties_list) 
                    : list.properties_list || [];
                
                // Add properties to selection using Set to avoid duplicates
                setFormData(prev => {
                    const currentSet = new Set(prev.properties_list);
                    listProperties.forEach(uuid => currentSet.add(uuid));
                    return {
                        ...prev,
                        properties_list: Array.from(currentSet)
                    };
                });
            }
        } else {
            // Remove list from selected lists
            setSelectedExistingLists(prev => prev.filter(uuid => uuid !== listUuid));
            
            // Note: We're keeping the properties selected even when unchecking the list
            // This allows users to uncheck a list but keep some of its properties
            // If you want to remove the properties, you'd need more complex logic to track
            // which properties came from which lists
        }
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
            cell: row => (
                <div className="d-flex align-items-center">
                    <FaList className="mr-2" />
                    <span>{row.name}</span>
                </div>
            )
        },
        {
            name: 'Properties Count',
            selector: row => {
                const list = typeof row.properties_list === 'string'
                    ? JSON.parse(row.properties_list)
                    : row.properties_list || [];
                return [...new Set(list)].length;
            },
            sortable: true,
            cell: row => {
                const list = typeof row.properties_list === 'string'
                    ? JSON.parse(row.properties_list)
                    : row.properties_list || [];
                return <span>{[...new Set(list)].length}</span>;
            }
        },
        {
            name: 'Created',
            selector: row => row.created_at,
            sortable: true,
            cell: row => row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'
        },
        {
            name: 'Actions',
            cell: row => (
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(row)}
                        title="Edit"
                        className="mr-2"
                    >
                        <FaEdit />
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(row)}
                        title="Delete"
                    >
                        <FaTrash />
                    </Button>
                </div>
            )
        }
    ];

    if (loading) {
        return (
            <div className="d-flex flex-row justify-content-start align-items-start">
                <NavSidebar />
                <div className="d-flex flex-column rounded-lg bg-white p-3 mt-2" style={{ width: '87vw', minWidth: '800px' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <Spinner animation="border" role="status">
                            <span className="sr-only">Loading...</span>
                        </Spinner>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex flex-row justify-content-start align-items-start">
            <NavSidebar />
            <div className="d-flex flex-column rounded-lg bg-white p-3 mt-2" style={{ width: '87vw', minWidth: '800px' }}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                        <Button
                            variant="link"
                            onClick={() => history.push('/migration')}
                            className="p-0 mr-3"
                        >
                            <MdArrowBack size={24} />
                        </Button>
                        <h2 className="mb-0">Property Groups</h2>
                    </div>
                    <Button
                        variant="primary"
                        onClick={handleCreate}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaPlus /> Create New List
                    </Button>
                </div>

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {successMessage && (
                    <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
                        {successMessage}
                    </Alert>
                )}

                <DataTable
                    columns={columns}
                    data={propertyLists}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 20, 30, 50]}
                    striped
                    highlightOnHover
                    pointerOnHover
                    responsive
                    noDataComponent={
                        <div className="text-center p-5">
                            <h5>No property lists found</h5>
                            <p className="text-muted">Click "Create New List" to get started</p>
                        </div>
                    }
                />

                <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {modalMode === 'create' ? 'Create Property Group' : 'Edit Property Group'}
                        </Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>List Name <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Enter list name"
                                    required
                                />
                            </Form.Group>

                            {/* Import from Existing Lists - Only show in CREATE mode */}
                            {modalMode === 'create' && propertyLists.length > 0 && (
                                <Form.Group className="mb-3">
                                    <div 
                                        className="d-flex align-items-center mb-2"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setShowImportSection(!showImportSection)}
                                    >
                                        {showImportSection ? <FaChevronDown /> : <FaChevronRight />}
                                        <span className="ms-2 fw-bold">
                                            Import from existing lists (optional)
                                        </span>
                                    </div>
                                    
                                    {showImportSection && (
                                        <div style={{ 
                                            maxHeight: '200px', 
                                            overflowY: 'auto', 
                                            border: '1px solid #dee2e6', 
                                            borderRadius: '0.25rem',
                                            padding: '0.5rem',
                                            backgroundColor: '#f8f9fa'
                                        }}>
                                            {propertyLists.map(list => {
                                                const listPropertiesCount = typeof list.properties_list === 'string' 
                                                    ? JSON.parse(list.properties_list).length 
                                                    : (list.properties_list || []).length;
                                                
                                                return (
                                                    <Form.Check
                                                        key={list.uuid}
                                                        type="checkbox"
                                                        id={`import-list-${list.uuid}`}
                                                        label={
                                                            <div>
                                                                <strong>{list.name}</strong>
                                                                <small className="text-muted d-block">
                                                                    {listPropertiesCount} {listPropertiesCount === 1 ? 'property' : 'properties'}
                                                                </small>
                                                            </div>
                                                        }
                                                        checked={selectedExistingLists.includes(list.uuid)}
                                                        onChange={() => handleExistingListToggle(list.uuid)}
                                                        className="mb-2"
                                                    />
                                                );
                                            })}
                                            {propertyLists.length === 0 && (
                                                <div className="text-muted text-center p-2">
                                                    No existing lists available
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    {showImportSection && selectedExistingLists.length > 0 && (
                                        <Form.Text className="text-muted">
                                            Properties from selected lists have been added to your selection below
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Properties ({formData.properties_list.length} selected)
                                </Form.Label>
                                
                                {/* Search Input */}
                                {!loadingProperties && availableProperties.length > 0 && (
                                    <InputGroup className="mb-3">
                                        <InputGroup.Text>
                                            <FaSearch />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search properties by name or address..."
                                            value={propertySearchTerm}
                                            onChange={(e) => setPropertySearchTerm(e.target.value)}
                                        />
                                        {propertySearchTerm && (
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => setPropertySearchTerm('')}
                                                title="Clear search"
                                            >
                                                <FaTimes />
                                            </Button>
                                        )}
                                    </InputGroup>
                                )}
                                
                                {loadingProperties ? (
                                    <div className="text-center p-3">
                                        <Spinner animation="border" size="sm" />
                                        <span className="ms-2">Loading properties...</span>
                                    </div>
                                ) : (
                                    <>
                                        {/* Show search results count when searching */}
                                        {propertySearchTerm && (
                                            <div className="mb-2 text-muted small">
                                                {sortedFilteredProperties.length === 0 
                                                    ? `No properties found matching "${propertySearchTerm}"`
                                                    : sortedFilteredProperties.length === 1
                                                    ? `1 property found matching "${propertySearchTerm}"`
                                                    : `${sortedFilteredProperties.length} properties found matching "${propertySearchTerm}"`
                                                }
                                            </div>
                                        )}
                                        
                                        <div style={{ 
                                            maxHeight: '400px', 
                                            overflowY: 'auto', 
                                            border: '1px solid #dee2e6', 
                                            borderRadius: '0.25rem',
                                            padding: '0.5rem'
                                        }}>
                                            {availableProperties.length === 0 ? (
                                                <div className="text-muted text-center p-3">
                                                    No properties available
                                                </div>
                                            ) : sortedFilteredProperties.length === 0 && propertySearchTerm ? (
                                                <div className="text-muted text-center p-3">
                                                    No properties found matching "{propertySearchTerm}"
                                                    <div className="small mt-2">
                                                        Try searching with a different term
                                                    </div>
                                                </div>
                                            ) : (
                                                sortedFilteredProperties.map((property, index) => {
                                                    const isSelected = formData.properties_list.includes(property.uuid);
                                                    const prevProperty = sortedFilteredProperties[index - 1];
                                                    const isPrevSelected = prevProperty && formData.properties_list.includes(prevProperty.uuid);
                                                    const showDivider = index > 0 && isPrevSelected && !isSelected;
                                                    
                                                    return (
                                                        <React.Fragment key={property.uuid}>
                                                            {showDivider && (
                                                                <hr className="my-2" style={{ borderColor: '#dee2e6' }} />
                                                            )}
                                                            <Form.Check
                                                                type="checkbox"
                                                                id={`property-${property.uuid}`}
                                                                label={
                                                                    <div>
                                                                        <strong>{property.name}</strong>
                                                                        {property.address && (
                                                                            <small className="text-muted d-block">
                                                                                {property.address}
                                                                            </small>
                                                                        )}
                                                                    </div>
                                                                }
                                                                checked={isSelected}
                                                                onChange={() => handlePropertyToggle(property.uuid)}
                                                                className="mb-2"
                                                            />
                                                        </React.Fragment>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </>
                                )}
                                <Form.Text className="text-muted">
                                    Select properties to include in this list
                                </Form.Text>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button 
                                variant="secondary" 
                                onClick={() => setShowModal(false)}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="primary" 
                                type="submit"
                                disabled={submitting || !formData.name.trim()}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="mr-2"
                                        />
                                        {modalMode === 'create' ? 'Creating...' : 'Saving...'}
                                    </>
                                ) : (
                                    modalMode === 'create' ? 'Create' : 'Save Changes'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </div>
        </div>
    );
};

export default PropertyListScreen;